import { useCallback, useMemo, useState } from "react";
import {
  Campaign,
  DEFAULT_EVENT_TEXTS_CAMPAIGN_ID,
  MassText,
  MassTextAttachment,
  MassTextType,
} from "@markit/common.types";
import { useSelector } from "react-redux";
import { getAccountSlice } from "../redux/slices/accountSlice";
import {
  deepCopy,
  isDelayedTriggerAutomation,
  makeEmptyMassText,
  makeEmptyTextAttachment,
  massTextMessageConverter,
  massTextPlaceholder,
  ONE_DAY_IN_MS,
} from "@markit/common.utils";
import { getMassTextAttachmentsSnap } from "../utils/FirebaseUtils";
import useAsyncOnMount from "./useAsyncEffectOnMount";
import { generate } from "shortid";
import { getEventSlice } from "../redux/slices/eventSlice";

type useMassTextManagementProps = {
  massText?: MassText;
  campaign?: Campaign;
  eventId?: string;
  isDuplicateText?: boolean;
};

export const useMassTextManagement = (props: useMassTextManagementProps) => {
  const { massText, campaign, eventId, isDuplicateText } = props;
  const { accountData } = useSelector(getAccountSlice);
  const { events } = useSelector(getEventSlice);
  const [massTextSettings, setMassTextSettings] = useState<MassText>(
    makeEmptyMassText(generate(), accountData.uid, accountData.fullName, {
      message: massTextPlaceholder(accountData.fullName),
      eventRefId: eventId,
      campaignId: campaign?.id,
    })
  );
  const [attachmentSettings, setAttachmentSettings] =
    useState<MassTextAttachment>(makeEmptyTextAttachment(generate()));
  const [loaded, setLoaded] = useState(false);

  const foundEvent = useMemo(() => {
    return events.find((event) => event.id === eventId);
  }, [events, eventId]);

  const updateMassTextSettings = useCallback(
    (newMassTextSettings: Partial<MassText>) => {
      setMassTextSettings((prevMassTextSettings) => {
        return { ...prevMassTextSettings, ...newMassTextSettings };
      });
    },
    []
  );

  const updateAttachmentSettings = useCallback(
    (newAttachmentSettings: Partial<MassTextAttachment>) => {
      setAttachmentSettings((prevAttachmentSettings) => {
        return { ...prevAttachmentSettings, ...newAttachmentSettings };
      });
    },
    []
  );

  const resetMassText = useCallback(() => {
    setMassTextSettings(
      makeEmptyMassText(generate(), accountData.uid, accountData.fullName, {
        eventRefId: eventId,
      })
    );
  }, [accountData.fullName, accountData.uid, eventId]);

  const resetMassTextAttachment = useCallback(() => {
    setAttachmentSettings(makeEmptyTextAttachment(generate()));
  }, []);

  useAsyncOnMount(async () => {
    const delayedTriggerAutomation =
      campaign && isDelayedTriggerAutomation(campaign, foundEvent);
    const emptyMassText = makeEmptyMassText(
      generate(),
      accountData.uid,
      accountData.fullName,
      {
        eventRefId: eventId,
        campaignId: isDuplicateText ? "" : campaign?.id,
        type: delayedTriggerAutomation
          ? MassTextType.TRIGGERTEXT
          : MassTextType.MASSTEXT,
        sentAtOffset:
          campaign && campaign.id === DEFAULT_EVENT_TEXTS_CAMPAIGN_ID
            ? -ONE_DAY_IN_MS
            : delayedTriggerAutomation
            ? ONE_DAY_IN_MS
            : 0,
      }
    );
    if (isDuplicateText && massText) {
      const oldAttachmentsSnap = await getMassTextAttachmentsSnap(
        accountData.uid,
        massText.campaignId,
        massText.id
      );
      const attachments = oldAttachmentsSnap.docs.map((doc) => doc.data());
      const duplicateText: MassText = {
        ...emptyMassText,
        message: massTextMessageConverter(
          massText.message,
          attachments,
          accountData.trustedSender
        ),
        catchUpEnabled: massText.catchUpEnabled,
        mediaUrl: massText.mediaUrl,
      };
      setMassTextSettings(duplicateText);
    } else {
      setMassTextSettings(massText ? deepCopy(massText) : emptyMassText);
    }
    setLoaded(true);
  });

  return {
    updateMassTextSettings,
    massTextSettings,
    makeEmptyMassText,
    attachmentSettings,
    updateAttachmentSettings,
    resetMassText,
    resetMassTextAttachment,
    loaded,
  };
};
